import { DateTime } from 'luxon';

/** Formato `OffsetDateTime` del API (`yyyy-MM-dd'T'HH:mm:ss.SSS'Z'`). */
const API_DATETIME_Z = "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'";

function localZone(): string {
  return DateTime.local().zoneName;
}

/** IANA time zone del navegador (p. ej. `America/Mexico_City`) para filtros de calendario del API. */
export function browserTimeZone(): string {
  return localZone();
}

/** Año calendario en la zona del navegador. */
export function browserCalendarYear(): number {
  return DateTime.local().year;
}

/** Mes calendario 1–12 en la zona del navegador. */
export function browserCalendarMonth(): number {
  return DateTime.local().month;
}

function extractCalendarDatePart(value: string): string | null {
  const match = value.trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return match?.[1] ?? null;
}

/**
 * Interpreta un valor del API como fecha de calendario en la zona local del navegador.
 * Usa solo la parte `yyyy-MM-dd` cuando existe, para evitar desfases por UTC.
 */
export function parseApiCalendarDate(value: string | null | undefined): DateTime | null {
  if (value == null || value === '') return null;
  const str = String(value).trim();
  const datePart = extractCalendarDatePart(str);
  if (datePart) {
    const dt = DateTime.fromISO(datePart, { zone: localZone() });
    return dt.isValid ? dt.startOf('day') : null;
  }
  const dt = DateTime.fromISO(str, { setZone: true }).setZone(localZone());
  return dt.isValid ? dt.startOf('day') : null;
}

/** Parsea `yyyy-MM-dd` u otro string del API como `Date` en calendario local. */
export function parseLocalDate(value: string | null | undefined): Date | null {
  const dt = parseApiCalendarDate(value);
  return dt?.toJSDate() ?? null;
}

/** Formatea fecha del API como `dd/MM/yyyy` en calendario local. */
export function formatApiDateOnly(value: string | null | undefined): string {
  return parseApiCalendarDate(value)?.toFormat('dd/MM/yyyy') ?? '';
}

/** `Date` → `yyyy-MM-dd` en calendario local. */
export function formatLocalDateOnly(d: Date): string {
  const dt = DateTime.fromJSDate(d, { zone: localZone() });
  return dt.isValid ? dt.toFormat('yyyy-MM-dd') : '';
}

/** Días de calendario desde hoy (local) hasta la fecha del API. */
export function calendarDaysUntil(value: string | null | undefined): number | null {
  const target = parseApiCalendarDate(value);
  if (!target) return null;
  const today = DateTime.local().startOf('day');
  return Math.round(target.diff(today, 'days').days);
}

/** Inicio del día local de hoy en formato Z que espera el API. */
export function startOfTodayApiZ(): string {
  return DateTime.local().startOf('day').toUTC().toFormat(API_DATETIME_Z);
}

/**
 * Rango de un mes calendario en UTC (`yyyy-MM`) para filtros del API.
 * Alineado con el agrupamiento mensual del backend (`AT TIME ZONE 'UTC'`).
 */
export function monthKeyToApiUtcRange(monthKey: string): { gte: string; lte: string } | null {
  const match = monthKey.trim().match(/^(\d{4})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return null;

  const monthStart = DateTime.utc(year, month, 1).startOf('month');
  const monthEnd = monthStart.endOf('month');
  if (!monthStart.isValid || !monthEnd.isValid) return null;

  return {
    gte: monthStart.toFormat(API_DATETIME_Z),
    lte: monthEnd.toFormat(API_DATETIME_Z)
  };
}

/**
 * Valor de datepicker / formulario → `yyyy-MM-dd` para el API.
 */
export function formValueToApiDateOnly(value: unknown): string | undefined {
  if (value == null || value === '') return undefined;
  if (typeof value === 'string') {
    const s = value.trim();
    const head = extractCalendarDatePart(s);
    if (head) return head;
    const parsed = parseApiCalendarDate(s);
    return parsed?.toFormat('yyyy-MM-dd');
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return formatLocalDateOnly(value);
  }
  return undefined;
}

/** Valor de datepicker / formulario → `Date` en hora local. */
export function formValueToLocalDate(value: unknown): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return parseLocalDate(formatLocalDateOnly(value));
  }
  if (typeof value === 'number' && !Number.isNaN(value)) {
    const dt = DateTime.fromMillis(value, { zone: localZone() });
    return dt.isValid ? parseLocalDate(dt.toFormat('yyyy-MM-dd')) : null;
  }
  if (typeof value === 'string') {
    const s = value.trim();
    const fromApi = parseApiCalendarDate(s);
    if (fromApi) return fromApi.toJSDate();
    const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
    if (dmy) {
      const [, day, month, year] = dmy;
      const fullYear = year.length === 2 ? 2000 + Number(year) : Number(year);
      const dt = DateTime.fromObject(
        { year: fullYear, month: Number(month), day: Number(day) },
        { zone: localZone() }
      );
      return dt.isValid ? dt.startOf('day').toJSDate() : null;
    }
  }
  return null;
}

/** Parsea ISO 8601 con zona horaria (`OffsetDateTime` del API). */
export function parseApiDateTime(value: string | null | undefined): Date | null {
  if (value == null || value === '') return null;
  const dt = DateTime.fromISO(String(value).trim(), { setZone: true });
  return dt.isValid ? dt.toJSDate() : null;
}

/**
 * Valor de formulario → ISO 8601 en UTC con sufijo `Z` para `OffsetDateTime` del API.
 * Usa medianoche local del día seleccionado (00:00:00).
 */
export function formValueToApiDateTime(value: unknown): string | undefined {
  const local = formValueToLocalDate(value);
  if (!local) return undefined;
  return DateTime.fromJSDate(local, { zone: localZone() })
    .startOf('day')
    .toUTC()
    .toFormat(API_DATETIME_Z);
}

/**
 * Valor de formulario → ISO 8601 en UTC con sufijo `Z`.
 * Usa las 24:00:00 locales del día seleccionado (inicio del día siguiente).
 */
export function formValueToApiDateTimeEndExclusive(value: unknown): string | undefined {
  const local = formValueToLocalDate(value);
  if (!local) return undefined;
  return DateTime.fromJSDate(local, { zone: localZone() })
    .startOf('day')
    .set({ hour: 24, minute: 0, second: 0, millisecond: 0 })
    .toUTC()
    .toFormat(API_DATETIME_Z);
}

/** 01/01/{year} 00:00:00 en zona local del navegador → UTC `Z` para el API. */
export function calendarYearStartToApiZ(year: number): string {
  return DateTime.fromObject({ year, month: 1, day: 1 }, { zone: localZone() })
    .startOf('day')
    .toUTC()
    .toFormat(API_DATETIME_Z);
}

/** 31/12/{year} 24:00:00 en zona local del navegador → UTC `Z` para el API. */
export function calendarYearEndExclusiveToApiZ(year: number): string {
  return DateTime.fromObject({ year, month: 12, day: 31 }, { zone: localZone() })
    .startOf('day')
    .set({ hour: 24, minute: 0, second: 0, millisecond: 0 })
    .toUTC()
    .toFormat(API_DATETIME_Z);
}

/**
 * Valor de formulario con hora → ISO 8601 en UTC (`OffsetDateTime` del API).
 * Conserva la hora del `Date` (no fuerza medianoche).
 */
export function formValueToApiInstant(value: unknown): string | undefined {
  if (value == null || value === '') return undefined;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return DateTime.fromJSDate(value, { zone: localZone() })
      .toUTC()
      .toFormat(API_DATETIME_Z);
  }
  if (typeof value === 'string') {
    const dt = DateTime.fromISO(value.trim(), { setZone: true });
    return dt.isValid ? dt.toUTC().toFormat(API_DATETIME_Z) : undefined;
  }
  return undefined;
}

/** Fecha y hora actual del navegador como `OffsetDateTime` (con offset local, no `Z`). */
export function browserNowOffsetDateTime(): string {
  return formatLocalDateTimeWithOffset(new Date());
}

/** Formatea un `Date` como ISO 8601 con offset local del navegador. */
export function formatLocalDateTimeWithOffset(d: Date): string {
  const iso = DateTime.fromJSDate(d, { zone: localZone() }).toISO();
  return iso ?? '';
}
